import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Grid, Card, CardContent, Button, useTheme, alpha, Paper,
  Container, Chip, Avatar, Divider, Tab, Tabs, List, ListItem, ListItemText,
  ListItemIcon, IconButton
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  LocationOn as LocationIcon,
  Business as BusinessIcon,
  Category as CategoryIcon,
  LocalOffer as OfferIcon,
  Email as EmailIcon,
  LinkedIn as LinkedInIcon,
  Twitter as TwitterIcon,
  Article as ArticleIcon,
  Info as InfoIcon,
  Work as WorkIcon,
  Message as MessageIcon,
  PersonAdd as PersonAddIcon
} from '@mui/icons-material';

import Sidebar from '../components/common/Sidebar';
import * as userApi from '../services/user';
import { User } from '../services/user';
import { useAuth } from '../context/AuthContext';
import MentorshipRequestModal from '../components/MentorshipRequestModal';
import * as articleApi from '../services/article';

const drawerWidth = 240; // Define the drawer width

const ExpertDetail = () => {
  const { id } = useParams(); // Changed from expertId to match route param
  const navigate = useNavigate();
  const theme = useTheme();
  const { user } = useAuth();

  const [expert, setExpert] = useState<User | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [relatedContent, setRelatedContent] = useState<(articleApi.Article | articleApi.Innovation)[]>([]);
  const [loading, setLoading] = useState(true);
  const [mentorshipModalOpen, setMentorshipModalOpen] = useState(false);
  const [contentLoading, setContentLoading] = useState(false);

  // Check if current user can interact with this expert
  // Experts can connect/message other experts
  // Members can only request mentorship
  const canInteract = user?.userType === 'expert';
  const isMember = user?.userType === 'member';

  useEffect(() => {
    // Get expert data - make sure id is non-null
    const fetchExpert = async () => {
      if (!id) return;

      try {
        setLoading(true);
        const expertData = await userApi.getExpertById(id);
        setExpert(expertData);
      } catch (error) {
        console.error('Error fetching expert:', error);
        navigate('/experts');
      } finally {
        setLoading(false);
      }
    };

    fetchExpert();
  }, [id, navigate]);

  // Fetch expert's articles and innovations
  useEffect(() => {
    const fetchExpertContent = async () => {
      if (!id) return;

      try {
        setContentLoading(true);

        // Fetch all approved articles and innovations
        const [articles, innovations] = await Promise.all([
          articleApi.getArticles(),
          articleApi.getInnovations()
        ]);

        // Filter by this expert's ID
        const expertArticles = articles.filter(article => article.authorId === id);
        const expertInnovations = innovations.filter(innovation => innovation.authorId === id);

        // Combine and set
        setRelatedContent([...expertArticles, ...expertInnovations]);
      } catch (error) {
        console.error('Error fetching expert content:', error);
      } finally {
        setContentLoading(false);
      }
    };

    fetchExpertContent();
  }, [id]);

  const handleTabChange = (_event: any, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleBackClick = () => {
    navigate(-1);
  };

  const handleConnectClick = () => {
    // Navigate to connections management or trigger connection request
    console.log('Connect with expert:', id);
    // TODO: Implement connection logic
  };

  const handleMessageClick = () => {
    // Navigate to messages page with this expert
    if (id) {
      navigate(`/messages/${id}`);
    }
  };

  const handleMentorshipRequest = () => {
    setMentorshipModalOpen(true);
  };

  const handleMentorshipSuccess = () => {
    // Optionally show a success message or update UI
    console.log('Mentorship request sent successfully');
  };

  // Function to navigate to the correct article page
  const handleArticleClick = (articleId: string, type: 'article' | 'innovation') => {
    navigate(`/article/${type}/${articleId}`);
  };

  // Show loading state while fetching expert data
  if (loading || !expert) {
    return (
      <Container maxWidth="lg" sx={{ py: 8, textAlign: 'center' }}>
        <Typography variant="h5">Loading expert information...</Typography>
      </Container>
    );
  }
  
  return (
    <Box sx={{ display: 'flex' }}>

        <Container maxWidth="lg">
          {/* Back Button */}
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleBackClick}
            sx={{ mb: 3 }}
          >
            Back to Experts
          </Button>

          {/* Profile Header */}
          <Paper
            elevation={2}
            sx={{
              mb: 4,
              borderRadius: 3,
              overflow: 'hidden'
            }}
          >
            <Box
              sx={{
                p: 4,
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.8)}, ${alpha(theme.palette.primary.dark, 0.9)})`,
                color: 'white',
                position: 'relative'
              }}
            >
              <Grid container spacing={4} alignItems="center">
                <Grid item xs={12} md="auto" sx={{ textAlign: { xs: 'center', md: 'left' } }}>
                  <Box sx={{
                    position: 'relative',
                    display: 'inline-block'
                  }}>
                    {/* Profile Photo */}
                    <Avatar
                      src={expert.photoUrl || `https://ui-avatars.com/api/?name=${expert.firstName}+${expert.lastName}&background=random&size=200` || "/assets/expert-placeholder.jpg"}
                      alt={`${expert.firstName} ${expert.lastName}`}
                      sx={{
                        width: 180,
                        height: 180,
                        border: `6px solid ${alpha(theme.palette.common.white, 0.3)}`,
                        boxShadow: '0 12px 24px rgba(0,0,0,0.3)',
                      }}
                    />

                    {/* Company Logo - Positioned at bottom right of profile photo */}
                    <Avatar
                      src={expert.companyLogoUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(expert.organizationName || 'Company')}&background=random&size=80&bold=true`}
                      alt={expert.organizationName || 'Company'}
                      variant="rounded"
                      sx={{
                        width: 80,
                        height: 80,
                        border: `4px solid ${theme.palette.primary.main}`,
                        boxShadow: '0 6px 16px rgba(0,0,0,0.25)',
                        position: 'absolute',
                        bottom: -10,
                        right: -10,
                        bgcolor: 'white'
                      }}
                    />
                  </Box>
                </Grid>

                <Grid item xs={12} md>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                    <Chip
                      label={expert.expertProfile?.tag || 'Expert'}
                      size="medium"
                      sx={{
                        bgcolor: theme.palette.secondary.main,
                        color: 'white',
                        fontWeight: 600,
                        px: 2,
                        height: 36
                      }}
                    />

                    <Chip
                      icon={<LocationIcon fontSize="small" sx={{ color: 'white !important' }} />}
                      label={expert.country}
                      size="medium"
                      sx={{
                        bgcolor: alpha(theme.palette.common.white, 0.25),
                        color: 'white',
                        fontWeight: 500,
                        height: 36
                      }}
                    />
                  </Box>

                  <Typography variant="h3" sx={{ fontWeight: 700, mb: 1.5, letterSpacing: '-0.5px' }}>
                    {`${expert.firstName} ${expert.lastName}`}
                  </Typography>

                  <Typography variant="h6" sx={{ opacity: 0.95, mb: 1, fontWeight: 500 }}>
                    {expert.title}
                  </Typography>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <BusinessIcon sx={{ opacity: 0.8, fontSize: 20 }} />
                    <Typography variant="h6" sx={{ opacity: 0.85, fontWeight: 500 }}>
                      {expert.organizationName}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', mt: 3, flexWrap: 'wrap' }}>
                    {expert.selectedSectors?.map((sector, idx) => (
                      <Chip
                        key={idx}
                        label={sector}
                        size="small"
                        sx={{
                          bgcolor: alpha(theme.palette.common.white, 0.15),
                          color: 'white',
                          mr: 1,
                          mb: 1
                        }}
                      />
                    ))}
                  </Box>
                </Grid>
              </Grid>
            </Box>
            
            {/* Quick Actions */}
            {(canInteract || isMember) && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, p: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
                {canInteract && (
                  <>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<PersonAddIcon />}
                      onClick={handleConnectClick}
                      sx={{
                        background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                        '&:hover': {
                          background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                          transform: 'translateY(-2px)',
                          boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}`
                        },
                        transition: 'all 0.3s ease'
                      }}
                    >
                      Connect
                    </Button>

                    <Button
                      variant="outlined"
                      color="primary"
                      startIcon={<MessageIcon />}
                      onClick={handleMessageClick}
                      sx={{
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.2)}`
                        },
                        transition: 'all 0.3s ease'
                      }}
                    >
                      Send Message
                    </Button>
                  </>
                )}

                {isMember && (
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<MessageIcon />}
                    onClick={handleMentorshipRequest}
                    sx={{
                      background: `linear-gradient(135deg, ${theme.palette.secondary.main}, ${theme.palette.secondary.dark})`,
                      '&:hover': {
                        background: `linear-gradient(135deg, ${theme.palette.secondary.dark}, ${theme.palette.secondary.main})`,
                        transform: 'translateY(-2px)',
                        boxShadow: `0 6px 20px ${alpha(theme.palette.secondary.main, 0.4)}`
                      },
                      transition: 'all 0.3s ease'
                    }}
                  >
                    Request Mentorship
                  </Button>
                )}
              </Box>
            )}
          </Paper>

          {/* Tabs and Content */}
          <Paper
            elevation={2}
            sx={{
              borderRadius: 3,
              overflow: 'hidden',
              mb: 4
            }}
          >
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                textColor="primary"
                indicatorColor="primary"
                sx={{
                  '& .MuiTab-root': {
                    minHeight: 64,
                    textTransform: 'none',
                    fontWeight: 500,
                    fontSize: '1rem'
                  }
                }}
              >
                <Tab icon={<InfoIcon />} iconPosition="start" label="About" />
                <Tab icon={<WorkIcon />} iconPosition="start" label="Experience" />
                <Tab icon={<ArticleIcon />} iconPosition="start" label="Content" />
              </Tabs>
            </Box>
            
            {/* Tab Panels */}
            <Box sx={{ p: 4 }}>
              {activeTab === 0 && (
                <Grid container spacing={4}>
                  <Grid item xs={12} md={8}>
                    {/* About Section */}
                    <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
                      Professional Summary
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary' }}>
                      {expert.experience}
                    </Typography>
                    
                    <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
                      Organization
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      {expert.organizationName}
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary' }}>
                      {expert.organizationBrief}
                    </Typography>
                    
                    <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
                      Areas of Expertise
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 4 }}>
                      {expert.selectedSectors?.map((sector, idx) => (
                        <Chip
                          key={idx}
                          label={sector}
                          variant="outlined"
                          color="primary"
                          sx={{ borderRadius: 2 }}
                        />
                      ))}
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12} md={4}>
                    <Card elevation={0} sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05), p: 2, borderRadius: 2 }}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Contact Information
                        </Typography>
                        
                        <List disablePadding>
                          <ListItem disableGutters sx={{ px: 0, py: 1 }}>
                            <ListItemIcon sx={{ minWidth: 40 }}>
                              <LocationIcon color="primary" />
                            </ListItemIcon>
                            <ListItemText
                              primary="Location"
                              secondary={expert.country}
                            />
                          </ListItem>
                          
                          <ListItem disableGutters sx={{ px: 0, py: 1 }}>
                            <ListItemIcon sx={{ minWidth: 40 }}>
                              <BusinessIcon color="primary" />
                            </ListItemIcon>
                            <ListItemText
                              primary="Organization"
                              secondary={expert.organizationName}
                            />
                          </ListItem>
                          
                          <ListItem disableGutters sx={{ px: 0, py: 1 }}>
                            <ListItemIcon sx={{ minWidth: 40 }}>
                              <CategoryIcon color="primary" />
                            </ListItemIcon>
                            <ListItemText
                              primary="Segment"
                              secondary={expert.segment}
                            />
                          </ListItem>
                        </List>
                      </CardContent>
                    </Card>
                    
                    <Card elevation={0} sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05), p: 2, borderRadius: 2, mt: 3 }}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Services Offered
                        </Typography>
                        
                        <List disablePadding>
                          {expert.expertProfile?.offers?.split(', ').map((offer, idx) => (
                            <ListItem key={idx} disableGutters sx={{ px: 0, py: 1 }}>
                              <ListItemIcon sx={{ minWidth: 40 }}>
                                <OfferIcon color="primary" />
                              </ListItemIcon>
                              <ListItemText
                                primary={offer}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              )}
              
              {activeTab === 1 && (
                <Box>
                  <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
                    Professional Experience
                  </Typography>
                  
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      borderRadius: 2,
                      border: `1px solid ${theme.palette.divider}`,
                      mb: 3
                    }}
                  >
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {expert.title}
                    </Typography>
                    <Typography variant="subtitle1" sx={{ color: theme.palette.primary.main, mb: 1 }}>
                      {expert.organizationName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Present • {expert.country}
                    </Typography>
                    <Typography variant="body1">
                      {expert.experience}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 3 }}>
                      {expert.selectedSectors?.map((sector, idx) => (
                        <Chip
                          key={idx}
                          label={sector}
                          size="small"
                          variant="outlined"
                          color="primary"
                        />
                      ))}
                    </Box>
                  </Paper>
                  
                  <Typography variant="body1" color="text.secondary" sx={{ mt: 4, fontStyle: 'italic' }}>
                    Contact {`${expert.firstName} ${expert.lastName}`} directly to request a full CV/resume.
                  </Typography>
                </Box>
              )}
              
              {activeTab === 2 && (
                <Box>
                  <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
                    Published Content
                  </Typography>

                  {contentLoading ? (
                    <Box sx={{ textAlign: 'center', py: 6 }}>
                      <Typography variant="body1" color="text.secondary">
                        Loading content...
                      </Typography>
                    </Box>
                  ) : relatedContent.length > 0 ? (
                    <Grid container spacing={3}>
                      {relatedContent.map((content) => {
                        // Determine if this is an innovation or article
                        const isInnovation = 'tags' in content && content.tags?.some(tag =>
                          ['innovation', 'spotlight'].some(keyword => tag.toLowerCase().includes(keyword))
                        );
                        const contentType = isInnovation ? 'innovation' : 'article';

                        return (
                          <Grid item xs={12} md={6} key={content.id}>
                            <Card
                              elevation={0}
                              sx={{
                                display: 'flex',
                                borderRadius: 2,
                                border: `1px solid ${theme.palette.divider}`,
                                height: '100%',
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                '&:hover': {
                                  boxShadow: theme.shadows[4],
                                  transform: 'translateY(-2px)'
                                }
                              }}
                              onClick={() => handleArticleClick(content.id, contentType)}
                            >
                              <Box
                                sx={{
                                  width: 120,
                                  height: 'auto',
                                  backgroundColor: alpha(
                                    isInnovation ? theme.palette.secondary.main : theme.palette.primary.main,
                                    0.1
                                  ),
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}
                              >
                                <ArticleIcon
                                  sx={{
                                    fontSize: 40,
                                    color: isInnovation ? theme.palette.secondary.main : theme.palette.primary.main
                                  }}
                                />
                              </Box>
                              <CardContent sx={{ flex: '1 0 auto', p: 2 }}>
                                {isInnovation && (
                                  <Chip
                                    label="Innovation Spotlight"
                                    size="small"
                                    sx={{
                                      mb: 1,
                                      bgcolor: alpha(theme.palette.secondary.main, 0.1),
                                      color: theme.palette.secondary.main
                                    }}
                                  />
                                )}
                                <Typography variant="subtitle1" fontWeight={600}>
                                  {content.title}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1, mt: 0.5 }}>
                                  {content.summary && content.summary.length > 100
                                    ? `${content.summary.substring(0, 100)}...`
                                    : content.summary}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {new Date(content.date || content.createdAt || '').toLocaleDateString()}
                                </Typography>
                              </CardContent>
                            </Card>
                          </Grid>
                        );
                      })}
                    </Grid>
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 6 }}>
                      <Typography variant="h6" color="text.secondary">
                        No published content yet
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Check back later for articles and innovation spotlights from this expert
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          </Paper>
          
          {/* Related Experts Section */}
          <Paper
            elevation={2}
            sx={{
              borderRadius: 3,
              overflow: 'hidden',
              mb: 4
            }}
          >
            <Box sx={{ p: 3, borderBottom: `1px solid ${theme.palette.divider}` }}>
              <Typography variant="h5" fontWeight={600}>
                Similar Experts
              </Typography>
            </Box>
            
            <Box sx={{ p: 3 }}>
              <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                This feature will be implemented in a future update.
              </Typography>
            </Box>
          </Paper>
        </Container>

        {/* Mentorship Request Modal */}
        {isMember && expert && user && (
          <MentorshipRequestModal
            open={mentorshipModalOpen}
            onClose={() => setMentorshipModalOpen(false)}
            expert={{
              id: expert.id,
              firstName: expert.firstName,
              lastName: expert.lastName,
              title: expert.title,
              organizationName: expert.organizationName,
              photoUrl: expert.photoUrl
            }}
            memberName={`${user.firstName} ${user.lastName}`}
            memberCompany={user.organizationName as string | undefined}
            memberTitle={user.title as string | undefined}
            onSuccess={handleMentorshipSuccess}
          />
        )}
      </Box>

  );
};

export default ExpertDetail;